import * as React from "react";

/**
 * Wiring for a drag-to-resize splitter.
 *
 * The obvious implementation — `onMouseDown={() => setIsResizing(true)}` plus `mousemove`
 * and `mouseup` listeners on `document` — gets three things wrong:
 *
 * 1. It never calls `preventDefault`, so alongside the resize the browser also starts its
 *    own press-and-drag gesture: extending a text selection from the caret nearest the
 *    handle out to wherever the pointer travels. That is what paints the nav pane,
 *    breadcrumb and toolbar blue mid-drag.
 * 2. Tracking the drag on `document` breaks over an iframe. The email preview and the
 *    external-resource view are both iframes sitting directly across a splitter, and once
 *    the pointer enters one the events go to *its* document instead: the resize freezes,
 *    and the `mouseup` in there never reaches us, so the handle stays armed until the user
 *    clicks somewhere else.
 * 3. `cursor: col-resize` lives on the 5px handle, so the cursor snaps back to an arrow
 *    whenever the pointer outruns it — which, during a resize, is most of the time.
 *
 * Pointer capture answers 2 and 3: the handle keeps receiving pointer events wherever the
 * pointer goes, including over iframes and outside the window, and the browser releases the
 * capture on pointerup, on pointercancel, or if the element unmounts mid-drag. 1 is answered
 * by cancelling `mousedown` — see the note on onMouseDown for why it must be that event and
 * not `pointerdown`.
 *
 * @param onResize called with the pointer's viewport coordinates for each move of an active
 *                 drag. It may be a fresh closure every render; it is read through a ref.
 */

/**
 * Set on `<body>` for the duration of a drag. See the `.db-resizing` rule in taskpane.css:
 * it forces the resize cursor window-wide so it does not flicker off the handle.
 */
const RESIZING_CLASS_NAME = "db-resizing";

export interface IResizeHandleProps {
    onMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void;
    onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => void;
    onPointerUp: (event: React.PointerEvent<HTMLDivElement>) => void;
    onPointerCancel: (event: React.PointerEvent<HTMLDivElement>) => void;
    onLostPointerCapture: (event: React.PointerEvent<HTMLDivElement>) => void;
    onDragStart: (event: React.DragEvent<HTMLDivElement>) => void;
}

export const useResizeHandle = (onResize: (clientX: number, clientY: number) => void): IResizeHandleProps => {
    // Held in a ref so the returned handlers keep a stable identity whether or not the
    // caller memoised its callback.
    const onResizeRef = React.useRef(onResize);
    onResizeRef.current = onResize;

    const activePointerId = React.useRef<number | undefined>(undefined);

    // A drag still running when the handle unmounts — the pane is collapsed, the user
    // navigates away — would otherwise leave the whole window stuck on the resize cursor.
    //
    // Conditional because more than one handle can be mounted at once (the side-pane
    // splitter and the Exchange preview's). An unconditional remove would let an
    // unrelated handle unmounting mid-drag strip the class off an ACTIVE drag.
    React.useEffect(() => () => {
        if (activePointerId.current !== undefined) {
            document.body.classList.remove(RESIZING_CLASS_NAME);
        }
    }, []);

    /**
     * The fix for the stray selection — and it lives on `mousedown`, not `pointerdown`,
     * deliberately.
     *
     * Cancelling `pointerdown` also works, but it suppresses the whole compatibility
     * mouse-event sequence, `mousedown` included. Fluent dismisses an open Callout or
     * ContextualMenu from a document-level `mousedown`, so cancelling the pointer event
     * left menus stuck open when the user grabbed the splitter. Letting `mousedown` fire
     * and cancelling ITS default suppresses exactly the selection gesture and nothing
     * else.
     */
    const onMouseDown = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (event.button !== 0) {
            return;
        }
        event.preventDefault();
    }, []);

    const onPointerDown = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        // Secondary buttons have to fall through: the context menu, and the mouse
        // back/forward buttons ContentManager listens for, are not resize gestures.
        if (event.button !== 0) {
            return;
        }

        activePointerId.current = event.pointerId;
        document.body.classList.add(RESIZING_CLASS_NAME);
        // Optional-called because jsdom, and webviews older than anything we support, have
        // no pointer capture. Losing it costs the iframe robustness, not the resize.
        event.currentTarget.setPointerCapture?.(event.pointerId);
    }, []);

    const onPointerMove = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (activePointerId.current !== event.pointerId) {
            return;
        }

        onResizeRef.current(event.clientX, event.clientY);
    }, []);

    const onPointerUp = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (activePointerId.current !== event.pointerId) {
            return;
        }

        const element = event.currentTarget;
        activePointerId.current = undefined;
        document.body.classList.remove(RESIZING_CLASS_NAME);

        // Browsers release capture on pointerup themselves; releasing explicitly also covers
        // the pointercancel path, which shares this handler.
        if (element.hasPointerCapture?.(event.pointerId)) {
            element.releasePointerCapture?.(event.pointerId);
        }
    }, []);

    const onDragStart = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        // A bare <div> is not draggable, but host chrome can still raise dragstart from a
        // press-and-drag; without this the splitter can be picked up as content mid-resize.
        event.preventDefault();
    }, []);

    return {
        onMouseDown,
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onPointerCancel: onPointerUp,
        // The spec's own "this pointer is no longer yours" signal, and the backstop for
        // every path that ends a drag without a pointerup we can see. It fires AFTER
        // pointerup, where the guard above makes it a no-op — so this only does work when
        // pointerup never came. That matters because the stuck state it prevents is a
        // window-wide `cursor: col-resize !important` with no way for the user to clear it.
        onLostPointerCapture: onPointerUp,
        onDragStart
    };
};
